import { useState } from "react";
import type { AlertSnooze } from "@/types/models";

// A collapsed list of the alerts the owner has muted, with an unsnooze action.
// Renders nothing when nothing is snoozed. Companion to NeedsAttentionPanel.
export default function SnoozedAlerts({
  snoozes,
  onUnsnooze,
}: {
  snoozes: AlertSnooze[];
  onUnsnooze: (snooze: AlertSnooze) => void;
}) {
  const [open, setOpen] = useState(false);
  if (snoozes.length === 0) return null;

  return (
    <div className="mb-6 card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
          Snoozed <span className="text-gray-400 dark:text-gray-500">({snoozes.length})</span>
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ul className="space-y-1 px-5 pb-4">
          {snoozes.map((s) => (
            <li
              key={`${s.rule_id}-${s.flock_id ?? ""}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 dark:border-gray-800/60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-700 dark:text-gray-200">{s.label}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{untilLabel(s.snoozed_until)}</p>
              </div>
              <button
                type="button"
                onClick={() => onUnsnooze(s)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-500/10"
              >
                Unsnooze
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function untilLabel(until?: string): string {
  if (!until) return "Snoozed until you unsnooze";
  const d = new Date(until);
  if (isNaN(d.getTime())) return "Snoozed";
  return `Snoozed until ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}
